import { environment } from '@root/environments/environment';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import { InfoBannerComponent } from '@custom-components/feedback/info-banner/info-banner.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Store } from '@ngrx/store';
import { Subject, SubscriptionLike } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import {
  BulkFirmwareRejectionReason,
  BulkStartFirmwareUpdateResponse,
  DeviceModel,
  FirmwareModel,
} from '@core/models';
import { TranslateModule } from '@ngx-translate/core';
import { FirmwareActions } from '@root/app/store/firmware.index';
import {
  selectBulkDlmsUpdateResponse,
  selectBulkUpdateResponse,
  selectFirmwareError,
} from '@root/app/store/selectors/firmware.selectors';
import { FirmwareSSEService } from '@core/services/firmware.sse.service';
import {
  bulkUpdateLimitFor,
  FIRMWARE_TRANSPORT,
  FirmwareTransport,
} from '@core/utils/firmware-catalogue.util';

export interface BulkFirmwareUpdateDialogData {
  selectedDevices: DeviceModel[];
  transport: FirmwareTransport;
  availableFirmwares: FirmwareModel[];
  /** Defaults to the transport's own cap; the page passes it explicitly. */
  maxDevices?: number;
}

interface BulkProgress {
  total: number;
  inProgress: number;
  succeeded: number;
  failed: number;
}

/** Rejection reasons device-func can return; anything else falls back to the raw message. */
const REJECTION_REASON_KEYS: Record<string, string> = {
  device_not_found: 'firmwareManagement.bulkUpdate.rejection.deviceNotFound',
  device_type_mismatch: 'firmwareManagement.bulkUpdate.rejection.deviceTypeMismatch',
} satisfies Record<BulkFirmwareRejectionReason, string>;

@Component({
  selector: 'xemex-bulk-firmware-update-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    SpinnerComponent,
    InfoBannerComponent,
    MatProgressBarModule,
    TranslateModule,
  ],
  templateUrl: './bulk-firmware-update-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bulk-firmware-update-dialog.component.scss',
})
export class BulkFirmwareUpdateDialogComponent implements OnDestroy {
  readonly dialogRef = inject(MatDialogRef<BulkFirmwareUpdateDialogComponent>);
  readonly data = inject<BulkFirmwareUpdateDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private eventSubscription?: SubscriptionLike;

  private prevSucceeded = 0;
  private prevFailed = 0;

  form: FormGroup;

  progress = signal<BulkProgress>({
    total: 0,
    inProgress: 0,
    succeeded: 0,
    failed: 0,
  });

  stage = signal<'form' | 'updating' | 'complete'>('form');

  /** DLMS has no streaming channel: the 202 body itself is the outcome. */
  readonly dlmsResult = signal<BulkStartFirmwareUpdateResponse | undefined>(undefined);

  /** The response only carries the numeric id; the LDN comes from the devices the user selected. */
  readonly rejections = computed(() => {
    const ldnById = new Map(this.data.selectedDevices.map(d => [String(d.deviceId), d.deviceLDN]));
    return (this.dlmsResult()?.rejected ?? []).map(r => ({
      deviceId: r.deviceId,
      deviceLabel: ldnById.get(String(r.deviceId)) || String(r.deviceId),
      reasonKey: REJECTION_REASON_KEYS[r.reason],
      message: r.message,
    }));
  });

  constructor(private readonly eventSourceService: FirmwareSSEService) {
    this.form = this.fb.group({
      firmwareId: [null, Validators.required],
    });
  }

  ngOnDestroy(): void {
    this.closeSseConnection();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isDlms(): boolean {
    return this.data.transport === FIRMWARE_TRANSPORT.Dlms;
  }

  get deviceCount(): number {
    return this.data.selectedDevices.length;
  }

  get maxDevices(): number {
    return this.data.maxDevices ?? bulkUpdateLimitFor(this.data.transport);
  }

  get exceedsDeviceLimit(): boolean {
    return this.deviceCount > this.maxDevices;
  }

  /** One firmware row belongs to exactly one device type, and the transport is decided from the
   * first selected device, so a selection spanning device types would push the wrong image over
   * the wrong backend. The device list is no longer filtered to a single type, so this is
   * reachable from the page. */
  get hasMixedDeviceTypes(): boolean {
    return new Set(this.data.selectedDevices.map(d => d.deviceTypeId)).size > 1;
  }

  get canSubmit(): boolean {
    return (
      this.form.valid &&
      this.deviceCount > 0 &&
      !this.exceedsDeviceLimit &&
      !this.hasMixedDeviceTypes
    );
  }

  get completedCount(): number {
    const p = this.progress();
    return p.succeeded + p.failed;
  }

  get progressPercent(): number {
    const p = this.progress();
    if (p.total === 0) return 0;
    return Math.round(((p.succeeded + p.failed) / p.total) * 100);
  }

  private get selectedFirmware(): FirmwareModel | undefined {
    const firmwareId = this.form.value.firmwareId as number | null;
    return this.data.availableFirmwares.find(fw => fw.firmwareId === firmwareId);
  }

  onUpdate(): void {
    const firmware = this.selectedFirmware;
    if (!this.canSubmit || !firmware) return;

    this.prevSucceeded = 0;
    this.prevFailed = 0;

    this.progress.set({
      total: this.deviceCount,
      inProgress: this.deviceCount,
      succeeded: 0,
      failed: 0,
    });
    this.stage.set('updating');

    if (this.isDlms) {
      this.startDlmsBulkUpdate(firmware);
      return;
    }
    this.startEnnyLcuBulkUpdate(firmware);
  }

  private startDlmsBulkUpdate(firmware: FirmwareModel): void {
    this.store.dispatch(
      FirmwareActions.bulkStartDlmsFirmwareUpdate({
        request: {
          deviceIds: this.data.selectedDevices.map(d => Number(d.deviceId)),
          firmwareId: firmware.firmwareId,
        },
      }),
    );

    this.store
      .select(selectFirmwareError)
      .pipe(
        filter(error => !!error),
        take(1),
      )
      .subscribe(() => this.handleBulkUpdateFailure());

    this.store
      .select(selectBulkDlmsUpdateResponse)
      .pipe(
        filter(response => !!response),
        take(1),
      )
      .subscribe(response => {
        this.dlmsResult.set(response);
        this.progress.set({
          total: response!.accepted.length + response!.rejected.length,
          inProgress: 0,
          succeeded: response!.accepted.length,
          failed: response!.rejected.length,
        });
        this.stage.set('complete');
        this.changeDetector.detectChanges();
      });
  }

  private startEnnyLcuBulkUpdate(firmware: FirmwareModel): void {
    this.store.dispatch(
      FirmwareActions.bulkUpdateDeviceFirmware({
        request: {
          device_ids: this.data.selectedDevices.map(d => String(d.deviceId)),
          device_type: this.data.transport,
          version: firmware.version,
        },
      }),
    );

    this.store
      .select(selectFirmwareError)
      .pipe(
        filter(error => !!error),
        take(1),
      )
      .subscribe(() => this.handleBulkUpdateFailure());

    this.store
      .select(selectBulkUpdateResponse)
      .pipe(
        filter(response => !!response?.bulk_update_id),
        take(1),
      )
      .subscribe(response => {
        if (response?.bulk_update_id) {
          this.connectToSse(response.bulk_update_id);
        }
      });
  }

  private handleBulkUpdateFailure(): void {
    this.closeSseConnection();

    const p = this.progress();

    this.progress.set({
      ...p,
      inProgress: 0,
      failed: p.total,
    });

    this.stage.set('complete');
  }

  onCancel(): void {
    this.closeSseConnection();
    this.dialogRef.close();
  }

  onClose(): void {
    this.closeSseConnection();
    this.dialogRef.close();
  }

  closeSseConnection(): void {
    this.eventSubscription?.unsubscribe();
  }

  private async connectToSse(bulkUpdateId: string): Promise<void> {
    const url = `${environment.api.url}/firmware/bulk-updates/${bulkUpdateId}/stream`;

    this.eventSubscription = this.eventSourceService
      .connectEventSource(
        url,
        {
          headers: {
            'api-version': 'v1',
          },
        },
        ['update'],
      )
      .subscribe({
        next: data => {
          try {
            const parsed = JSON.parse(data.data);

            const succeeded = parsed['succeeded'] as number;
            const inProgress = parsed['in_progress'] as number;
            const failed = parsed['failed'] as number;

            if (succeeded !== undefined && inProgress !== undefined && failed !== undefined) {
              const newSucceeded = succeeded - this.prevSucceeded;
              const newFailed = failed - this.prevFailed;

              this.progress.update(p => ({
                ...p,
                inProgress,
                succeeded,
                failed,
              }));
              this.changeDetector.detectChanges();

              for (let i = 0; i < newSucceeded; i++) {
                const deviceId = this.data.selectedDevices[this.prevSucceeded + i]?.deviceId;
                if (deviceId) {
                  this.store.dispatch(
                    FirmwareActions.updateBulkProgress({
                      deviceId: String(deviceId),
                      status: 'success',
                    }),
                  );
                }
              }

              for (let i = 0; i < newFailed; i++) {
                const deviceId =
                  this.data.selectedDevices[this.prevSucceeded + newSucceeded + i]?.deviceId;
                if (deviceId) {
                  this.store.dispatch(
                    FirmwareActions.updateBulkProgress({
                      deviceId: String(deviceId),
                      status: 'failed',
                    }),
                  );
                }
              }

              this.prevSucceeded = succeeded;
              this.prevFailed = failed;

              this.checkCompletion();
            }
          } catch (e) {
            console.error('Failed to parse SSE message:', e);
          }
        },
        error: () => {
          this.handleBulkUpdateFailure();
        },
      });
  }

  private checkCompletion(): void {
    const p = this.progress();
    if (p.succeeded + p.failed === p.total) {
      this.stage.set('complete');
      this.changeDetector.detectChanges();
    }
  }
}
